'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '@/utils/cropImage';
import { DedicationImage } from '@/utils/svgIcons';
import CustomToast from '../CustomToast';
import toast from 'react-hot-toast';
import Image from 'next/image';
import { TailSpin } from 'react-loader-spinner';
import { useTranslation } from 'next-i18next';
import useUserPreferences from '@/hooks/useUserPreferences';
import { useQuery } from 'react-query';
import { GetSettings } from '@/services/api/settings';
import ImageGuidelinesPopup from './ImageGuidelinesPopup';
import { useCurrency } from '../DesktopHeader';

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (err) => reject(err);
  });
};

interface ImageUploaderProps {
  croppedImage: string | null;
  setCroppedImage: (img: string | null) => void;
  imageSelected: string;
  setImageSelected: (value: string) => void;
  isEdit?: any;
  uuid?: any;
  product?: any;
  effectiveImageSelected?: any;
  croppedFile: File | null;
  setCroppedFile: (file: File | null) => void;
  imageInEdit?: any;
  aiStory?: boolean;
  forAiStory?: boolean;
  isDedicationStep?: boolean;
  useAiImageForDedication?: boolean;
  setUseAiImageForDedication?: (value: boolean) => void;
  aiStoryImage?: string | null;
  dedicationImageMode?: any
  setDedicationImageMode?: any
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  croppedImage,
  setCroppedImage,
  imageSelected,
  setImageSelected,
  isEdit,
  uuid,
  product,
  effectiveImageSelected,
  croppedFile,
  setCroppedFile,
  imageInEdit,
  aiStory = false,
  forAiStory = false,
  isDedicationStep = false,
  useAiImageForDedication = true,
  setUseAiImageForDedication,
  aiStoryImage = null,
  dedicationImageMode,
  setDedicationImageMode
}) => {
  const {t} = useTranslation('personalizeModal');
  const { locale } = useRouter();
  const preferences = useUserPreferences();
  const { data: settings, isLoading: isSettingsLoading } = useQuery(
    ['settings', preferences?.countryCode, preferences?.currencyCode],
    () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  );
  
  const [imageSrc, setImageSrc] = useState<string | null>(
    isEdit === '1' ? croppedImage : null
  );
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [showGuidelines, setShowGuidelines] = useState(false);

  const router = useRouter();
  const isNotebookRoute = router.pathname.includes('/notebook');

  // Determine context for display
  const isAiStoryGeneration = aiStory && !isDedicationStep;
  const isAiDedication = aiStory && isDedicationStep;
  const isRegularDedication = !aiStory && isDedicationStep;
  React.useEffect(()=>{
    if(isAiStoryGeneration && !isAiDedication && !isRegularDedication){
      setShowGuidelines(true);
    }
  }, [])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.custom((tEl) => (
        <CustomToast
          t={tEl}
          message={t('pictureSizeError')}
          type="error"
        />
      ));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      console.log('Calling image validation API...');
      
      const res = await fetch('/api/validate-image', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      console.log('Validation response:', data);

      if (!res.ok || data.error) {
        setLoading(false);
        toast.custom((tEl) => (
          <CustomToast
            t={tEl}
            message={t('imageQualityLowError')}
            type="error"
          />
        ));
        return;
      }

      setLoading(false);
      const reader = new FileReader();
      reader.onload = () => {
        console.log('Setting image source and showing cropper');
        setImageSrc(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      setLoading(false);
      toast.custom((tEl) => (
        <CustomToast
          t={tEl}
          message={t('imageUploadError')}
          type="error"
        />
      ));
    }

    setOriginalFile(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      'image/jpeg': ['.jpg', '.jpeg'], 
      'image/png': ['.png'] 
    },
    multiple: false
  });

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    console.log('Crop complete:', croppedAreaPixels);
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    console.log('Handling crop...');
    
    if (!originalFile || !croppedAreaPixels || !imageSrc) {
      console.error('Missing required data for cropping');
      return;
    }

    try {
      console.log('Using direct canvas cropping...');
      
      // Create image element - use different name to avoid conflict with Next.js Image
      const imgElement = new window.Image(); // Use window.Image to access native DOM Image
      imgElement.src = imageSrc;
      
      // Wait for image to load
      await new Promise((resolve, reject) => {
        imgElement.onload = resolve;
        imgElement.onerror = reject;
        // Timeout to prevent hanging
        setTimeout(() => reject(new Error('Image load timeout')), 10000);
      });
      
      console.log('Image loaded successfully, dimensions:', imgElement.width, 'x', imgElement.height);
      
      // Create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }
      
      // Validate crop dimensions against image
      const maxX = imgElement.width - 1;
      const maxY = imgElement.height - 1;
      
      // Ensure crop coordinates are within bounds
      const crop = {
        x: Math.max(0, Math.min(croppedAreaPixels.x, maxX)),
        y: Math.max(0, Math.min(croppedAreaPixels.y, maxY)),
        width: Math.min(croppedAreaPixels.width, imgElement.width - croppedAreaPixels.x),
        height: Math.min(croppedAreaPixels.height, imgElement.height - croppedAreaPixels.y)
      };
      
      console.log('Crop area after validation:', crop);
      
      if (crop.width <= 0 || crop.height <= 0) {
        throw new Error(`Invalid crop dimensions: ${crop.width}x${crop.height}`);
      }
      
      // Set canvas size
      canvas.width = crop.width;
      canvas.height = crop.height;
      
      console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);
      console.log('Drawing cropped image on canvas...');
      
      // Draw the cropped portion
      ctx.drawImage(
        imgElement,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
      );
      
      console.log('Image drawn successfully');
      console.log('Converting canvas to blob...');
      
      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log('Blob created, size:', blob.size, 'type:', blob.type);
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          originalFile.type || 'image/jpeg',
          0.95
        );
      });
      
      // Create file
      const croppedFile = new File([blob], originalFile.name, {
        type: originalFile.type || 'image/jpeg'
      });
      
      console.log('Cropped file created:', croppedFile.name, croppedFile.size, croppedFile.type);
      
      // Convert to base64 for preview
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          console.log('Base64 conversion complete');
          resolve(reader.result as string);
        };
        reader.onerror = (error) => {
          console.error('FileReader error:', error);
          reject(new Error('Failed to read file as base64'));
        };
        reader.readAsDataURL(croppedFile);
      });
      
      console.log('Base64 length:', base64.length);
      console.log('Setting state...');
      
      // Set state
      if (typeof setCroppedImage === 'function') {
        setCroppedImage(base64);
      } else {
        console.error('setCroppedImage is not a function:', setCroppedImage);
      }
      
      if (typeof setCroppedFile === 'function') {
        setCroppedFile(croppedFile);
      } else {
        console.error('setCroppedFile is not a function:', setCroppedFile);
      }
      
      if (setDedicationImageMode && typeof setDedicationImageMode === 'function') {
        setDedicationImageMode('separate-image');
        console.log('Dedication image mode set to separate-image');
      }
      
      setShowCropper(false);
      console.log('Cropper closed');
      
      toast.custom((tEl) => (
        <CustomToast
          t={tEl}
          message={t('imageUploadSuccess')}
          type="success"
        />
      ));
      
    } catch (error: any) {
      console.error('Crop error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // More specific error messages
      let userMessage = t('imageCropError');
      if (error.message.includes('Invalid crop dimensions')) {
        userMessage = 'Please adjust the crop area - it appears to be outside the image bounds.';
      } else if (error.message.includes('Image load timeout')) {
        userMessage = 'The image took too long to load. Please try again with a smaller image.';
      } else if (error.message.includes('Failed to create blob')) {
        userMessage = 'Failed to process the cropped image. Please try again.';
      }
      
      toast.custom((tEl) => (
        <CustomToast
          t={tEl}
          message={userMessage}
          type="error"
        />
      ));
    }
  };

  const resetImage = () => {
    localStorage.removeItem(`dedicationImageUrl-${uuid}`);
    setCroppedImage(null);
    setCroppedFile(null);
    setImageSrc(null);
    setZoom(1);
    setImageSelected('no');
  };

  const currencySymbol = useCurrency(settings?.image_price?.currency || "ILS");
  const imagePrice = settings?.image_price?.amount || 0;

  // Render AI story image toggle for dedication step
  const renderAiDedicationToggle = () => {
    if (!isAiDedication) return null;

    return (
      <div className="mb-4 space-y-3">
        <p className={`${locale === 'ar' ? 'text-right': 'text-left'} text-sm font-medium mb-2`}>
          {locale === 'ar' ? 'اختر خيار صورة الإهداء:' : 'Choose dedication image option:'}
        </p>
        
        <div className="space-y-2">
          {/* Option 1: Use AI image */}
          <div
            className={`p-3 border rounded-lg cursor-pointer transition-all ${
              dedicationImageMode === 'ai-image'
                ? 'border-primary bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setDedicationImageMode('ai-image')}
          >
            <div className={`${locale === 'ar' ? 'text-right': 'text-left'} flex items-center justify-between`}>
              <div>
                <p className="font-medium">
                  {locale === 'ar' ? 'استخدام صورة القصة الذكية' : 'Use AI story image'}
                </p>
                <p className="text-sm text-gray-500">
                  {locale === 'ar' ? 'بدون تكلفة إضافية' : 'No additional cost'}
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                dedicationImageMode === 'ai-image'
                  ? 'bg-primary border-primary'
                  : 'border-gray-300'
              }`}>
                {dedicationImageMode === 'ai-image' && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* Option 2: Upload separate image */}
          <div
            className={`p-3 border rounded-lg cursor-pointer transition-all ${
              dedicationImageMode === 'separate-image'
                ? 'border-primary bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => {
              setDedicationImageMode('separate-image');
              setShowGuidelines(true);
            }}
          >
            <div className={`${locale === 'ar' ? 'text-right': 'text-left'} flex items-center justify-between`}>
              <div>
                <p className="font-medium">
                  {locale === 'ar' ? 'رفع صورة مختلفة' : 'Upload different image'}
                </p>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-gray-500">
                    {locale === 'ar' ? 'سعر إضافي:' : 'Additional price:'}
                  </span>
                  <span className="text-primary font-medium">
                    {imagePrice}+{currencySymbol?.symbol || '₪'}
                  </span>
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                dedicationImageMode === 'separate-image'
                  ? 'bg-primary border-primary'
                  : 'border-gray-300'
              }`}>
                {dedicationImageMode === 'separate-image' && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* Option 3: No image */}
          <div
            className={`p-3 border rounded-lg cursor-pointer transition-all ${
              dedicationImageMode === 'no-image'
                ? 'border-primary bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => {
              setDedicationImageMode('no-image');
              if (!forAiStory) {
                localStorage.removeItem(`dedicationImageUrl-${uuid}`);
              }
              setCroppedImage(null);
              setImageSrc(null);
              setShowGuidelines(false);
            }}
          >
            <div className={`${locale === 'ar' ? 'text-right': 'text-left'} flex items-center justify-between`}>
              <div>
                <p className="font-medium">
                  {locale === 'ar' ? 'بدون صورة' : 'No image'}
                </p>
                <p className="text-sm text-gray-500">
                  {locale === 'ar' ? 'نص الإهداء فقط' : 'Dedication text only'}
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                dedicationImageMode === 'no-image'
                  ? 'bg-primary border-primary'
                  : 'border-gray-300'
              }`}>
                {dedicationImageMode === 'no-image' && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Show AI story image preview when using same image */}
        {dedicationImageMode === 'ai-image' && aiStoryImage && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700 mb-2">
              {locale === 'ar'
                ? 'سيتم استخدام هذه الصورة للإهداء:'
                : 'This image will be used for dedication:'}
            </p>
            <div className="flex items-center gap-3">
              <img
                src={aiStoryImage}
                alt="AI Story Image"
                className="w-16 h-16 object-cover rounded border-2 border-blue-300"
              />
              <div className="flex-1">
                <p className="text-xs text-gray-600">
                  {locale === 'ar'
                    ? 'الصورة المستخدمة في توليد القصة الذكية'
                    : 'Image used for AI story generation'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Determine heading and subheading based on context
  const getHeadings = () => {
    if (isAiStoryGeneration) {
      return {
        heading: t('aiStoryImageUploadLabel'),
        subheading: t('aiStoryImageUploadHelp')
      };
    } else if (isAiDedication) {
      return {
        heading: locale === 'ar' ? 'صورة الإهداء' : 'Dedication Image',
        subheading: locale === 'ar' 
          ? 'اختر الصورة التي تريد استخدامها للإهداء' 
          : 'Choose the image you want to use for dedication'
      };
    } else {
      return {
        heading: t('imageUploaderHeading'),
        subheading: t('imageUploaderSubHeading')
      };
    }
  };

  const headings = getHeadings();

  return (
    <>
      <div className="w-full lg:mt-0 rounded-lg p-4 bg-white">
        {(!aiStory && !isDedicationStep) && (
          <div className="flex flex-col items-start justify-center gap-1 lg:gap-0 mb-4">
            <div className="flex items-center justify-center gap-2">
              <DedicationImage />
              <h2 className={`text-md font-semibold ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                {headings.heading}
              </h2>
            </div>
            <h4 className={`text-sm font-light ${locale === 'ar' ? 'text-right mr-8' : 'text-left ml-8'}`}>
              {headings.subheading}
            </h4>
          </div>
        )}

        {/* Render AI dedication toggle if in AI dedication step */}
        {/* {renderAiDedicationToggle()} */}

        {/* Show radio button for non-AI stories */}
        {(!aiStory && product?.product_type === 1) && (
          <div className="flex flex-col gap-2">
            <div
              className={`rounded-md border-[1px] ${
                imageSelected === 'yes'
                  ? 'border-primary'
                  : 'border-[#E6E9ED]'
              } flex flex-row-reverse items-center justify-end gap-2 p-4 bg-white cursor-pointer`}
              onClick={() => {
                if (imageSelected === 'yes') {
                  setImageSelected('no');
                  if (setDedicationImageMode) {
                    setDedicationImageMode('no-image');
                  }
                  if (!forAiStory) {
                    localStorage.removeItem(`dedicationImageUrl-${uuid}`);
                  }
                  setCroppedImage(null);
                  setImageSrc(null);
                  setShowGuidelines(false);
                } else {
                  setImageSelected('yes');
                  if (setDedicationImageMode) {
                    setDedicationImageMode('separate-image');
                  }
                  setShowGuidelines(true);
                }
              }}
            >
              <div 
                className="flex flex-col items-start justify-center"
                id='dedication-image-selection'
              >
                <label className="text-sm text-black font-normal">
                  {t('addImage')}
                </label>
                <label className="text-base text-primary">
                  {settings?.image_price?.amount || 0}+{currencySymbol?.symbol || '₪'}
                </label>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="dedication"
                  checked={imageSelected === 'yes'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setImageSelected('yes');
                      if (setDedicationImageMode) {
                        setDedicationImageMode('separate-image');
                      }
                      setShowGuidelines(true);
                    } else {
                      setImageSelected('no');
                      if (setDedicationImageMode) {
                        setDedicationImageMode('no-image');
                      }
                      setCroppedImage(null);
                      setImageSrc(null);
                      setShowGuidelines(false);
                    }
                  }}
                  className="hidden peer"
                />
                <div
                  className={`w-5 h-5 rounded-full ${
                    imageSelected === 'yes'
                      ? 'bg-[#00214D]'
                      : 'bg-[#0CDFBF]'
                  } flex items-center justify-center relative`}
                >
                  {imageSelected === 'yes' && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="10"
                      viewBox="0 0 12 10"
                      fill="none"
                    >
                      <path
                        d="M1 5.19922L3.70368 8.14736C4.1159 8.59686 4.83101 8.57584 5.21611 8.10291L11 1"
                        stroke="#FFFFFF"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
              </label>
            </div>
          </div>
        )}

        {showGuidelines && (
          <ImageGuidelinesPopup onClose={() => setShowGuidelines(false)} />
        )}

        {/* Show uploader when needed */}
        {((isAiStoryGeneration) || 
          (isAiDedication && dedicationImageMode === 'separate-image') ||
          (isRegularDedication && imageSelected === 'yes')) && (
          <div className="mt-4">
            {!croppedImage ? (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed ${
                  isDragActive ? 'border-primary' : 'border-gray-400'
                } rounded-lg p-6 cursor-pointer bg-white flex flex-col items-center justify-center transition-colors`}
                id='image-uploader-dropzone'
              >
                <input {...getInputProps()} />
                {loading ? (
                  <div className="relative z-10 flex flex-col items-center justify-center text-center">
                    <TailSpin
                      height="28"
                      width="28"
                      color="#00214D"
                      ariaLabel="tail-spin-loading"
                      radius="1"
                      visible={true}
                    />
                    <p className="mt-4 text-md font-semibold text-primary">
                      {locale === 'ar' ? 'يرجى الانتظار، فنحن نقوم بالتحقق من الصورة.' : 'Please wait, we are checking the image.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-2 mx-auto flex items-center justify-center">
                      <span className="bg-[#00214D14] p-2 rounded-md">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="28"
                          height="28"
                          viewBox="0 0 28 28"
                          fill="none"
                        >
                          <path
                            d="M4.66699 19.8335V22.1668C4.66699 23.4555 5.71166 24.5002 7.00033 24.5002H21.0003C22.289 24.5002 23.3337 23.4555 23.3337 22.1668V19.8335"
                            stroke="#272727"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8.16699 10.4998L14.0003 4.6665L19.8337 10.4998"
                            stroke="#272727"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14.0003 4.6665V18.6665"
                            stroke="#272727"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                    <p className="text-[#B0BAC8] my-2 text-sm text-center">
                      {isDragActive 
                        ? t('dropImageHere') 
                        : isAiStoryGeneration
                          ? t('aiStoryUploadText') 
                          : t('imageInfoText')}
                    </p>
                    <button 
                      type="button"
                      className="bg-[#0CDFBF] text-primary text-base font-medium px-6 py-2 rounded-lg mx-auto hover:opacity-90 transition-opacity"
                    >
                      {isAiStoryGeneration 
                        ? t('uploadPhotoForAI') 
                        : isAiDedication
                          ? t('uploadDedicationImage')
                          : t('imageUploaderButtonText')}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('supportedFormats')}: JPG, PNG (Max 5MB)
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 border-2 border-dashed border-gray-400 rounded-lg p-3 bg-white">
                <img
                  src={croppedImage}
                  alt={isAiStoryGeneration ? "AI Story Image" : "Dedication Image"}
                  className="rounded border-2 border-primary w-[144px] h-[144px] object-cover"
                />
                <button
                  onClick={resetImage}
                  className="bg-[#0CDFBF] text-primary text-base font-medium px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
                >
                  {t('changeImageText')}
                </button>
                {isAiStoryGeneration && (
                  <p className="text-xs text-gray-500 text-center">
                    {t('imageRequiredForAI')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Show message when using AI image for dedication */}
        {isAiDedication && dedicationImageMode === 'ai-image' && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-700 text-center">
              {locale === 'ar'
                ? '✓ سيتم استخدام صورة القصة الذكية للإهداء. لن يتم تطبيق رسوم إضافية.'
                : '✓ Will use AI story image for dedication. No additional charges will apply.'}
            </p>
          </div>
        )}

        {/* Cropper Modal */}
        {showCropper && imageSrc && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <button
                onClick={() => {
                  setShowCropper(false);
                  resetImage();
                }}
                className={`absolute top-3 ${locale == 'ar' ? 'left-3' : 'right-3'} bg-[#93A3B03D] text-xl p-2 rounded-md z-50 hover:bg-gray-200 transition-colors`}
                aria-label="Close cropper"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.23742 7.00023L13.7439 1.88765C14.0857 1.57026 14.0857 1.05566 13.7439 0.738291C13.4021 0.420895 12.8479 0.420895 12.5061 0.738291L7.00022 5.8514L1.49438 0.738291C1.15257 0.420895 0.598385 0.420895 0.256602 0.738291C-0.0851811 1.05569 -0.0852084 1.57028 0.256602 1.88765L5.76302 7.00023L0.256602 12.1128C-0.0852084 12.4302 -0.0852084 12.9448 0.256602 13.2622C0.598412 13.5796 1.15259 13.5796 1.49438 13.2622L7.00022 8.14905L12.5061 13.2622C12.8479 13.5796 13.4021 13.5796 13.7438 13.2622C14.0857 12.9448 14.0857 12.4302 13.7438 12.1128L8.23742 7.00023Z"
                    fill="black"
                  />
                </svg>
              </button>

              <h3 className={`text-lg font-semibold mb-4 ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('cropYourImage')}
              </h3>

              <div className="relative w-full h-64 bg-gray-100 rounded-md overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="rect"
                  showGrid={true}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="mt-4 flex flex-col items-center">
                <div className="flex items-center justify-center gap-2 w-full mb-2">
                  <span className="text-sm">{t('zoom')}:</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full"
                  />
                  <span className="text-sm w-12">{zoom.toFixed(1)}x</span>
                </div>
                
                <p className="text-sm text-gray-600 mb-4 text-center">
                  {t('adjustImagePosition')}
                </p>

                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => {
                      setShowCropper(false);
                      resetImage();
                    }}
                    className="flex-1 border border-gray-300 text-gray-700 text-base font-medium px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleCrop}
                    className="flex-1 bg-primary text-white text-base font-medium px-4 py-3 rounded-lg hover:opacity-90 transition-opacity"
                  >
                    {t('confirmImageAdditionText')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ImageUploader;